import { Check, Copy01, CornerUpLeft } from "@untitled-ui/icons-react";
import { type MouseEvent as ReactMouseEvent } from "react";
import { useTranslation } from "react-i18next";

import DoubleCheck from "@/components/icons/DoubleCheck";
import FileTypeIcon from "@/components/icons/FileTypeIcon";
import MessageStatusClock from "@/components/icons/MessageStatusClock";
import MessageStatusWarning from "@/components/icons/MessageStatusWarning";
import UserAvatar from "@/components/media/UserAvatar";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { cn } from "@/lib/utils";
import { formatTime } from "@/utils";
import { convertBytesToReadable } from "@/utils/convertBytesToReadable";

import { type Message } from "../../types";
import {
  dispatchGroupInviteLink,
  parseGroupInviteToken,
} from "../../utils/groupInvite";
import VoiceMessagePlayer from "../voice/VoiceMessagePlayer";

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

function MessageContentRenderer({
  content,
  isMe,
}: {
  content: string;
  isMe: boolean;
}) {
  const parts = content.split(URL_REGEX);
  URL_REGEX.lastIndex = 0;

  const handleLinkClick = (
    event: ReactMouseEvent<HTMLAnchorElement>,
    url: string,
  ) => {
    const token = parseGroupInviteToken(url);
    if (!token) return;
    const handled = dispatchGroupInviteLink(token);
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <span className="whitespace-pre-line">
      {parts.map((part, i) => {
        if (URL_REGEX.test(part)) {
          URL_REGEX.lastIndex = 0;
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => handleLinkClick(e, part)}
              className={cn(
                "break-all underline",
                isMe ? "text-white" : "text-primary-600",
              )}
            >
              {part}
            </a>
          );
        }
        return part;
      })}
    </span>
  );
}

export default function MessageCard({
  message,
  isMe,
  showAvatar = true,
  showSenderName = true,
  isGrouped = false,
  isGroupedBelow = false,
  isRead = false,
  isGroup = false,
  className,
  onReply,
  onReplyPreviewClick,
}: {
  message: Message<true>;
  isMe: boolean;
  showAvatar?: boolean;
  showSenderName?: boolean;
  isGrouped?: boolean;
  isGroupedBelow?: boolean;
  isRead?: boolean;
  isGroup?: boolean;
  className?: string;
  onReply?: (message: Message<true>) => void;
  onReplyPreviewClick?: (messageId: number) => void;
}) {
  const { t } = useTranslation("messenger.user");
  const isPending = message.status === "pending";
  const isError = message.status === "error";
  const shouldShowDoubleTick =
    !isPending && !isError && (isGroup || (isMe && isRead));
  const shouldShowSingleTick = !isPending && !isError && isMe && !isRead;

  const replyTo = message.reply_to;
  const replyUserName = replyTo
    ? `${replyTo.user.first_name} ${replyTo.user.last_name}`
    : "";

  const handleOpenFile = () => {
    if (message.file?.url) window.open(message.file.url, "_blank");
  };

  const handleCopyMessage = () => {
    if (!message.content) return;

    navigator.clipboard.writeText(message.content).catch(() => {});
  };

  const handleMessageClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!window.matchMedia("(max-width: 767px)").matches) return;

    event.preventDefault();

    event.currentTarget.dispatchEvent(
      new MouseEvent("contextmenu", {
        bubbles: true,
        cancelable: true,
        clientX: event.clientX,
        clientY: event.clientY,
        button: 2,
      }),
    );
  };

  const isLastInGroup = !isGroupedBelow;
  const bubbleRounded = isMe
    ? isLastInGroup
      ? "rounded-xl rounded-br-none"
      : "rounded-xl"
    : isLastInGroup
      ? "rounded-xl rounded-bl-none"
      : "rounded-xl";

  const voiceFile =
    message.type === "voice" && message.file?.url ? message.file : null;

  const messageContent = (
    <div className="flex flex-col gap-1">
      {!isMe && isGroup && showSenderName && (
        <span className="text-primary-700 text-sm font-semibold">
          {message.user.first_name} {message.user.last_name}
        </span>
      )}

      {voiceFile ? (
        <VoiceMessagePlayer
          messageId={message.id}
          url={voiceFile.url}
          duration={voiceFile.duration}
          isMe={isMe}
          userName={`${message.user.first_name} ${message.user.last_name}`}
          userPosition={message.user.position?.name || null}
        />
      ) : (
        (message.file || message.content) && (
          <div className="flex flex-col gap-2">
            {message.file && (
              <div className="flex items-center gap-2">
                <FileTypeIcon size="md" extension={message.file.extension} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {message.file.url ? (
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenFile();
                        }}
                        className={cn(
                          "m-0 cursor-pointer border-0 bg-transparent p-0 hover:underline",
                          isMe ? "text-white" : "text-primary-600",
                        )}
                      >
                        {message.file.file_name}
                      </button>
                    ) : (
                      message.file.file_name
                    )}
                  </div>
                  <div className="text-sm">
                    {convertBytesToReadable(message.file.size)}
                  </div>
                </div>
              </div>
            )}
            {message.content && (
              <MessageContentRenderer content={message.content} isMe={isMe} />
            )}
          </div>
        )
      )}

      <div className="flex items-center gap-1">
        {isMe && (
          <div>
            {isPending ? (
              <MessageStatusClock className="text-primary-200" />
            ) : isError ? (
              <MessageStatusWarning className="text-primary-200" />
            ) : shouldShowDoubleTick ? (
              <DoubleCheck className="text-primary-200 size-3" />
            ) : shouldShowSingleTick ? (
              <Check className="text-primary-200 size-3" />
            ) : null}
          </div>
        )}
        <span
          className={cn(
            "text-xs font-medium",
            isMe ? "text-primary-200" : "text-gray-600",
          )}
        >
          {formatTime(message.created_at)}
        </span>
      </div>
    </div>
  );

  return (
    <div
      id={`msg-${message.id}`}
      className={cn(
        "flex w-full gap-3",
        isMe
          ? "flex-row-reverse rtl:flex-row"
          : "flex-row rtl:flex-row-reverse",
        !isMe && isGroup && "items-end",
        isGrouped ? "mt-1" : "mt-4",
        className,
      )}
    >
      {!isMe &&
        isGroup &&
        (showAvatar ? (
          <UserAvatar user={message.user} size="md" disableTooltip />
        ) : (
          <div className="size-10 shrink-0" />
        ))}

      <div className="flex max-w-xl min-w-20 flex-col wrap-break-word">
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div
              data-message-bubble
              data-own={isMe ? "true" : "false"}
              onClick={handleMessageClick}
              className={cn(
                "flex flex-col border px-3.5 py-2.5",
                bubbleRounded,
                replyTo ? "gap-3" : "gap-1",
                isMe
                  ? "bg-primary-600 border-primary-200 text-white"
                  : "border-gray-200 bg-white",
              )}
            >
              {replyTo && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onReplyPreviewClick?.(replyTo.id);
                  }}
                  className={cn(
                    "rounded-md border border-s-[3px] px-3 py-2 text-start text-sm",
                    isMe
                      ? "bg-primary-500 border-primary-500 border-s-primary-700 text-white"
                      : "border-s-primary-500 bg-gray-50 text-gray-600",
                  )}
                >
                  <div className="truncate font-semibold">{replyUserName}</div>
                  <div className="truncate">
                    {replyTo.content ||
                      (replyTo.type === "file" ? t("File") : "")}
                  </div>
                </button>
              )}

              {messageContent}
            </div>
          </ContextMenuTrigger>

          <ContextMenuContent
            className="flex min-w-40 flex-col"
            onCloseAutoFocus={(event) => event.preventDefault()}
          >
            <ContextMenuItem
              disabled={!message.content}
              onSelect={handleCopyMessage}
            >
              {t("Copy")}
              <Copy01 className="size-4 text-gray-400" />
            </ContextMenuItem>
            <ContextMenuItem onSelect={() => onReply?.(message)}>
              {t("Reply")}

              <CornerUpLeft className="size-4 text-gray-400 rtl:-scale-x-100" />
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      </div>
    </div>
  );
}
